/**
 * The order ticket.
 *
 * ```
 *  ┌────────────────┐
 *  │  BUY │  SELL   │   ← the side, B7.5
 *  │  UP  │  DOWN   │   ← <OutcomeToggle>, and the odds display
 *  ├────────────────┤
 *  │ Market│ Limit  │
 *  │ Price   0.63   │   ← limit only
 *  │ Shares  100    │
 *  │ [25][50][75]%  │
 *  │ Cost    $63.00 │   ← <PayoutPreview>
 *  │ ...            │
 *  │ ┌────────────┐ │
 *  │ │ BUY UP ▲   │ │
 *  │ └────────────┘ │
 *  └────────────────┘
 * ```
 *
 * ## Selling, and the one thing it is not — B7.5, `docs/06` §F43 and §F44
 *
 * A sell of shares the account does not hold **is not signed as a sell**: under
 * the CTF exchange a SELL was a transfer of outcome tokens the seller had to
 * hold, and an uncovered one reverted on chain. Since `docs/06` §F93 the
 * settlement contract settles an uncovered sell correctly, but the rewrite is
 * kept for v1 (CLAUDE.md invariant 21). So the ticket rewrites it into a buy of
 * the complement before signing — `domain/sell-shape.ts` is the rule and the whole
 * argument, and this component only calls it and renders what comes back.
 *
 * Two consequences live here rather than there.
 *
 * **A sell is quoted against the complement's ladder**, because that is where
 * its counterparty is (§7.8: `SELL UP @ 51` and `BUY DOWN @ 49` are one interest
 * on one book). So `quote` below is a quote of the complement whenever the side
 * is SELL, and every price the user sees is transformed back through `100 − p`
 * on the way out — §7.8's "every price a user sees is in the terms they traded
 * in", which is a rule about rendering and not about the wire.
 *
 * **The rewrite is explained here and nowhere else.** The venue will echo a buy
 * back on the order row, the fill and the receipt, correctly, because after the
 * rewrite the user genuinely did place one — and this client does not relabel
 * it, because relabelling needs intent that does not survive a reload or a
 * second tab, and a row that renames itself on refresh is worse than one that
 * never did. `describeSell()` is the sentence, and it is shown *before* the
 * click.
 *
 * ## Three things this component does not do
 *
 * **It does not decide whether the button is enabled.** `validateOrder()` does,
 * and this renders whatever comes back — the label, the helper line and the
 * affordance. A component that re-derived any part of that would be a second
 * opinion about the tick, the minimum size or the collar, and §F21m is what
 * happens when a decision function and its caller drift apart.
 *
 * **It does not stop accepting orders early.** There is no freeze (D6): the last
 * second of a market is legitimately tradeable and is the second users care
 * about most. The only clock-shaped disables come out of `validateOrder`, and
 * they are the two the spec names — the final `taker_delay_ms` for a *market*
 * order, and D9's clock-skew guard.
 *
 * **It does not raise a wallet popup on Buy.** Registration happens on **first
 * ticket focus**, not on the click (plan §Phase 5, docs/03 §3.2) — putting a
 * modal between an intent and its execution costs the user the market they were
 * aiming at, and a market lasts sixty seconds. That trigger is
 * the one piece of Phase 5's scope Phase 6 inherited, and it is `onFocusCapture`
 * below. The Settings entry point stays: a user who declined needs somewhere to
 * change their mind.
 *
 * ## What survives a roll (register §F22g)
 *
 * At `T+60` the market this ticket points at is gone. **Size, outcome and order
 * type survive; the limit price does not.** Size and outcome are the user's
 * intent and are market-independent — retyping them every sixty seconds, 1,440
 * times a day, is the dominant cost. A limit price is a statement about a
 * specific book that no longer exists, and carrying one into the next market is
 * how a user places an order they did not mean to place.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Glyph, Input, SegmentedControl, Slider } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { useWallet } from '@/components/wallet/WalletProvider'
import { OutcomeToggle } from './OutcomeToggle'
import { OneTapPanel } from './OneTapPanel'
import { PayoutPreview } from './PayoutPreview'
import {
  useBook,
  useOrders,
  useBalance,
  usePendingOrders,
  usePositions,
  useRuntime,
  useRuntimeState,
  useTicker,
} from '@/app/use-runtime'
import { useStore, useStoreField } from '@/app/use-store'
import { setClickToTrade } from '@/app/preferences'
import { publishTicketLink } from '@/stores/ticket-link'
import { announceSelfMatch } from '@/trading/announce'
import { uiStore } from '@/stores/ui'
import { useServerNow } from '@/app/use-server-now'
import { formatFixed, SHARES_DP, type Outcome } from '@/lib/decimal'
import { formatCents, formatShares } from '@/lib/format'
import { quoteOrder, saleProceedsMicroUsdc, type Quote } from '@/domain/quote'
import { oneTapPrice } from '@/domain/one-tap'
import { priceTextForOtherOutcome } from '@/domain/ticket-price'
import {
  complementOf,
  complementPriceCents,
  coverableShares,
  describeSell,
  splitSell,
  type OrderLeg,
} from '@/domain/sell-shape'
import {
  floorToStep,
  isIdentityAction,
  requiredMicroUsdc,
  validateOrder,
  type GateCode,
  type OrderGate,
  type OrderGateInput,
} from '@/domain/order-validation'
import { ticketGate } from '@/wallet/identity'
import { missingSetup } from '@/domain/trading-setup'
import {
  accountStatusOf,
  affordableShares,
  availableMicroUsdcOf,
  countOpen,
  microSharesOf,
  openInterestOf,
  positionSharesOf,
  restingSellShares,
  screeningBlocked,
} from '@/domain/order-inputs'
import type { MarketState } from '@/domain/lifecycle'
import type { TradingLimits } from '@/domain/order-config'
import type { Runtime } from '@/app/runtime'

export type OrderKind = 'MARKET' | 'LIMIT'

export interface OrderTicketProps {
  marketId: string | null
  symbol: string
  marketState: MarketState
  openAtMs: number
  expiryAtMs: number
  /**
   * The market's two CTF position ids, as decimal strings off the `Market`
   * payload. **Read, never derived** (invariant 11, §F26l) — which is why they
   * arrive as props rather than being computed from `marketId`.
   *
   * `null` before the market object has landed; the ticket is gated on
   * `marketId` anyway, and submitting without one is impossible.
   */
  upTokenId: string | null
  downTokenId: string | null
  limits: TradingLimits
  /**
   * The socket has been down for more than two seconds (docs/07 §3).
   *
   * **This is the connection's opinion and it is not the whole gate.** A `seq`
   * gap and a checksum mismatch both leave the socket `OPEN` while this
   * market's ladder stops being maintained, so the ticket adds the book's own
   * sync state to this before deciding — see `bookStale` below.
   */
  socketStale: boolean
  /**
   * The outcome the ticket opens on — `DOWN` when the user pressed BUY DOWN.
   *
   * **Read once, at mount**, and never again: after that the toggle is the
   * user's, and it survives the roll like the size does (§F22g above). That is
   * enough because the only caller that passes it is the sheet, which is not
   * mounted while closed — so every press of the sticky bar mounts a fresh
   * ticket. Without it both sticky buttons opened on `UP`.
   */
  initialOutcome?: Outcome
  /**
   * This is the ticket in its own column beside the standard orderbook, which
   * can trade from a click (`<BookPanel>`): publish the outcome and the Shares
   * box to it, and render the "Click to trade" box under Shares.
   *
   * Only `<TradeRoute>`'s column ticket sets it. The sheet below 1120px never
   * does — there the book is read-only, because a click would place a size the
   * user cannot see.
   */
  linkBook?: boolean
  className?: string
}

const TYPE_SEGMENTS = [
  { id: 'MARKET' as const, label: 'Market' },
  { id: 'LIMIT' as const, label: 'Limit' },
]

const SIDE_SEGMENTS = [
  { id: 'BUY' as const, label: 'Buy' },
  { id: 'SELL' as const, label: 'Sell' },
]

export type OrderSide = 'BUY' | 'SELL'

export const OrderTicket = memo(function OrderTicket({
  marketId,
  symbol,
  marketState,
  openAtMs,
  expiryAtMs,
  upTokenId,
  downTokenId,
  limits,
  socketStale,
  initialOutcome,
  linkBook = false,
  className,
}: OrderTicketProps) {
  const runtime = useRuntime()
  /**
   * The clock, subscribed **here** rather than threaded from `<TradeRoute>`.
   *
   * The gate needs a live instant — `TAKER_DELAY_NO_TIME` fires inside the last
   * 250 ms and `CLOCK_RESYNC` inside the last 500 — so the ticket genuinely
   * re-renders at 4 Hz and always did. What it must not do is make the *screen*
   * re-render at 4 Hz, which a `nowMs` prop did: `memo` compares props, so a
   * number that changes four times a second is a memo that can never bail out,
   * and the route above it was subscribed to the same clock to supply it
   * (`docs/17`, invariant 2).
   */
  const nowMs = useServerNow()
  // Subscribed here rather than threaded from `<TradeRoute>` as a prop, for
  // invariant 2's own reason read the other way: this store changes on rare
  // transitions (a connection status, a roll retarget), and the ticket already
  // re-renders at the book's rate, so reading it here costs nothing and adds no
  // prop three files have to carry.
  const runtimeState = useRuntimeState()
  const wallet = useWallet()
  const identity = useStore(runtime.identityStore)

  // Each of these is subscribed *here*, in the panel that renders it, and not in
  // `<TradeRoute>` — invariant 2. The book is the fast one, and it re-renders
  // this component and nothing above it.
  // `version` is not decoration. `useBook` returns the **live, mutated** state
  // object precisely because the ladder is not reconciled (plan §2.3), so
  // `book`'s identity never changes while a market runs — the version is the
  // only thing that says "look again", and the quote below depends on it.
  const { book, version: bookVersion } = useBook(marketId)
  const ticker = useTicker(marketId)
  const balance = useBalance()
  const { rows: positions } = usePositions()
  const { rows: orders } = useOrders()
  const { rows: pending } = usePendingOrders()

  const [outcome, setOutcome] = useState<Outcome>(initialOutcome ?? 'UP')
  const [side, setSide] = useState<OrderSide>('BUY')
  const [orderType, setOrderType] = useState<OrderKind>('MARKET')
  const [sizeText, setSizeText] = useState('')
  const [priceText, setPriceText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const prompted = useRef(false)
  /**
   * The double-click guard, and it is a ref rather than the `submitting` state.
   *
   * `<Button loading>` disables itself, but `setSubmitting(true)` only takes
   * effect at the next render — and a second click can land inside that window.
   * §6.2's promise is "two clicks, one order", and it is delivered by the
   * `Idempotency-Key`; that only works if the second click never mints a
   * *second* identity, because two identities are two logical orders by
   * definition (§6.3) and a server that placed both would be right to.
   *
   * A ref is read and written synchronously in the same tick as the click, which
   * is the only thing that closes the window. Measured rather than assumed: the
   * browser test rage-clicks and asserts one `client_order_id` ever reaches the
   * wire — and caught this, because the disabled button did not.
   */
  const inFlight = useRef(false)

  /**
   * The roll, per §F22g. `marketId` changing *is* the roll as far as this
   * component is concerned: the price is cleared, the size and outcome are not.
   *
   * Adjusted during render against a remembered prop rather than in an effect.
   * An effect would paint one frame with the previous market's limit price still
   * in the field — a stale price on a new book, which is precisely the thing
   * clearing it exists to prevent — and would fire a second render to fix it.
   * This is React's documented shape for "reset state when a prop changes".
   */
  const [renderedMarketId, setRenderedMarketId] = useState(marketId)
  if (renderedMarketId !== marketId) {
    setRenderedMarketId(marketId)
    setPriceText('')
  }
  // Switching Buy/Sell keeps the price: it is the same number on both tabs
  // (spec owner, 2026-10-14). Switching outcome carries it across as the
  // other outcome's price, `100 − P` — in the click handler below, not here,
  // because it is a response to the user's switch and not to a prop.

  // The wallet's network only gates arming: once a key is armed it signs the
  // orders and the wallet's network no longer matters (`ticketGate`).
  const gate = ticketGate(identity, wallet.chainMismatch !== null)

  /**
   * Registration on first focus, and exactly once per mount.
   *
   * `onFocusCapture` rather than a click handler because the trigger is
   * *focus* — the user reaching for the ticket, before they have committed to a
   * side or a size. `walletBusy` is checked because the header's Connect button
   * and this are the same action from two places, and two popups for one key is
   * the failure Phase 5's exit criterion counts.
   */
  const onFocus = useCallback(() => {
    // Reaching for the ticket is intent, so this is where a returning user's
    // wallet stack loads to read what setup is still missing (invariant 13).
    if (gate === 'READY') void wallet.refreshChainState(true)
    if (prompted.current || gate !== 'ENABLE_TRADING' || identity.walletBusy) return
    prompted.current = true
    wallet.promptEnableTrading()
  }, [gate, identity.walletBusy, wallet])

  const sizeShares = useMemo(() => microSharesOf(sizeText), [sizeText])

  /**
   * The book reads the outcome, the side and the size from here, because a
   * click on it places this order at the row's price (`stores/ticket-link.ts`).
   * The ticket keeps owning all three — this only publishes them, so its own
   * rules for a roll and a remount are unchanged.
   */
  useEffect(() => {
    if (linkBook) publishTicketLink({ outcome, side, sizeShares })
  }, [linkBook, outcome, side, sizeShares])

  const limitPriceCents = orderType === 'LIMIT' ? centsOf(priceText) : null

  /**
   * The outcome and price the *book* is asked about.
   *
   * A sell's counterparty rests on the complement's ladder (§7.8), so a
   * `SELL UP @ 51` is quoted as a `BUY DOWN @ 49` — which is not an
   * approximation, it is the same interest on the same book. Everything below
   * this line therefore works in buy terms, and the two places a user sees a
   * price transform back through `100 − p`.
   */
  const quoteOutcome = side === 'SELL' ? complementOf(outcome) : outcome
  const quoteLimitCents =
    limitPriceCents === null ? null
    : side === 'SELL' ? complementPriceCents(limitPriceCents)
    : limitPriceCents

  /**
   * The quote, re-taken whenever the **book** moves and not only when the form
   * does.
   *
   * `bookVersion` is the load-bearing dependency and it was missing until Phase
   * 7 (register §F25k). `book` is a mutated object whose identity is stable for
   * the life of a market, so with only `[book, …form…]` in the list this memo
   * ran once per keystroke and then froze: `<PayoutPreview>`'s cost, the collar
   * check (deleted since, §F117) and `INSUFFICIENT_LIQUIDITY` were all quoting the ladder as it stood
   * when the size was last typed, against a book that moves 20 times a second.
   *
   * It looked correct for four phases because the ticket had only ever been
   * exercised inside one market, where the first computation was against a full
   * book. **The roll is what made it visible**: at `T+60` the ticket re-targets
   * a book that is empty for one frame while the market maker quotes (38–64 ms),
   * the memo computed `0 shares available` there, and — nothing being left to
   * invalidate it — the ticket stayed `INSUFFICIENT LIQUIDITY` for the whole of
   * the next market.
   */
  const bookQuote = useMemo<Quote>(
    () =>
      quoteOrder(book, {
        outcome: quoteOutcome,
        side: 'BUY',
        sizeShares,
        limitPriceCents: quoteLimitCents,
        takerFeeRate4dp: limits.takerFeeRate4dp,
      }),
    // The rule calls `bookVersion` unnecessary because it cannot see that `book`
    // is mutated in place — which is the whole defect. Suppressed with the
    // reason attached, because "unnecessary" is exactly what the next person to
    // tidy this list would conclude.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      book,
      bookVersion,
      quoteOutcome,
      sizeShares,
      quoteLimitCents,
      limits.takerFeeRate4dp,
    ],
  )

  /**
   * **Order entry is gated on the book, not only on the socket.**
   *
   * `socketStale` is the connection's opinion and it misses the two failures
   * that matter most here: a `seq` gap and a checksum mismatch both leave the
   * socket `OPEN` while this market's ladder stops being maintained. The first
   * means the ladder is missing a delta; the second means it was *misapplied*
   * and the sequence never noticed — which is the bug class that actually
   * ships. Either way the quote above is computed from a book the client knows
   * is wrong, on a 1¢ tick, on a market that settles in under a minute.
   *
   * The sequencer already publishes exactly this through `markStale`, and both
   * causes terminate in it, which is what makes this one term rather than two
   * (docs/03 §9.2.1: *order entry against them is disabled*). It is read off
   * the store this component already subscribes for the quote, so the gate
   * costs no re-render that the ladder was not already causing — and
   * `bookVersion` is what says to look again, because `book` is mutated in
   * place (invariant 17).
   */
  const bookStale = socketStale || (book?.stale ?? false)

  /**
   * The price this sell is signed at, **in the user's own outcome's terms**.
   *
   * For a limit order it is what they typed. For a market order it is the
   * complement of the bound the quote respected — 99¢ in buy terms since §F117
   * deleted the collar, so 1¢ in sell terms — because the contract expresses price as a
   * ratio and has no way to say "at the market" (§F27d). Deriving it from the
   * quote's own bound rather than from the touch is what keeps the signature and
   * the body agreeing about the user's slippage tolerance, which is the defect
   * §F28 records for the buy side of this same choice.
   */
  const sellPriceCents =
    limitPriceCents !== null ? limitPriceCents
    : bookQuote.boundPriceCents === null ? null
    : complementPriceCents(bookQuote.boundPriceCents)

  const availableMicroUsdc = useMemo(() => availableMicroUsdcOf(balance), [balance])
  /**
   * The position cap is scored against the leg this order **grows**, which for
   * a sell is the complement: selling UP you do not hold mints DOWN (§7.8).
   */
  const positionShares = useMemo(
    () => positionSharesOf(positions, marketId, quoteOutcome),
    [positions, marketId, quoteOutcome],
  )
  const openOrders = useMemo(() => countOpen(orders, marketId), [orders, marketId])

  /**
   * How many shares of `outcome` this account can actually deliver — the
   * position, less every share its own live and pending sells have already
   * promised.
   *
   * That subtraction is `matcher.coveredSplit`'s `reservedClose` on this side of
   * the wire, and it is what stops two covered sells of one position both being
   * signed as transfers. Pending orders count: a submit with no receipt yet is a
   * promise this tab has already made, and is exactly the beat the projection is
   * behind.
   */
  const deliverableShares = useMemo(
    () =>
      coverableShares(
        positionSharesOf(positions, marketId, outcome),
        restingSellShares(orders, pending, marketId, outcome),
      ),
    [positions, orders, pending, marketId, outcome],
  )

  /**
   * The one or two orders this sell is actually signed as. Empty for a buy,
   * which is the one shape that needs no rewrite at all.
   */
  const legs = useMemo<OrderLeg[]>(
    () =>
      side === 'SELL' && sellPriceCents !== null ?
        splitSell({
          outcome,
          sizeShares,
          priceCents: sellPriceCents,
          coverableShares: deliverableShares,
          minOrderShares: limits.minOrderShares,
          sizeStep: limits.sizeStep,
        })
      : [],
    [side, outcome, sizeShares, sellPriceCents, deliverableShares, limits.minOrderShares, limits.sizeStep],
  )
  // The covered part, in the terms `validateOrder` and `<PayoutPreview>` want.
  // Derived from the legs rather than recomputed, so there is exactly one
  // answer to "how much of this closes" and it is the one that gets signed.
  const coveredShares = useMemo(
    () => legs.filter((leg) => leg.side === 'SELL').reduce((sum, leg) => sum + leg.sizeShares, 0n),
    [legs],
  )

  /**
   * **A market order is quoted with the user's money, as the venue now fills
   * it** — `docs/04` §7.7's budgeted market order (2026-09-30). The venue holds
   * what the account has rather than `size × bound`, pays each share at the
   * price it fills at and stops where the money runs out, so the preview, the
   * checks and the slider all walk the book the same way. The covered part of
   * a sell is free and fills first. A limit order locks at its own price and
   * keeps `bookQuote`.
   *
   * `bookQuote` stays the source of the bound: it does not depend on size or
   * money, and the sell's legs — which this quote's free shares come from — are
   * derived from it.
   */
  const quote = useMemo<Quote>(
    () =>
      orderType === 'MARKET' && availableMicroUsdc !== null ?
        quoteOrder(book, {
          outcome: quoteOutcome,
          side: 'BUY',
          sizeShares,
          limitPriceCents: null,
            takerFeeRate4dp: limits.takerFeeRate4dp,
          budgetMicroUsdc: availableMicroUsdc,
          freeShares: coveredShares,
          sizeStep: limits.sizeStep,
        })
      : bookQuote,
    // `bookVersion` for the reason `bookQuote` gives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      orderType,
      availableMicroUsdc,
      bookQuote,
      book,
      bookVersion,
      quoteOutcome,
      sizeShares,
      coveredShares,
      limits.takerFeeRate4dp,
      limits.sizeStep,
    ],
  )

  const input: OrderGateInput = {
    gate,
    accountStatus: accountStatusOf(identity.session),
    geoBlocked: screeningBlocked(identity.session),
    sessionKeyTtlMs: runtime.sessionKeyTtlMs,
    limits,
    marketState,
    nowMs,
    openAtMs,
    expiryAtMs,
    socketStale: bookStale,
    // The venue published a signing domain this client will not sign against
    // (§F48e). Read off `RuntimeState` rather than from the wallet chunk,
    // because the ticket renders for a visitor whose wallet stack has never
    // loaded — and the answer is the same for all of them.
    signingBlocked: runtimeState.signingBlocked,
    // A release or a restart has the venue down: refused before the click
    // (`docs/06` §F132).
    venueMaintenance: runtimeState.venueStatus === 'MAINTENANCE',
    // §9.2.10. The venue's own count off the previous order response, for
    // whichever of the order buckets is closest to empty — the sustained one and
    // the final-seconds one are both in `config.risk_limits.rate_limits`, and
    // the tighter of the two is the one that will reject.
    rateAllowance: orderAllowance(runtime, limits.orderRatePolicies),
    clockOffsetMs: runtime.clock.measuredOffsetMs,
    orderType,
    // Both in the terms the quote is in — a sell is validated as the buy of the
    // complement it will be signed as, which is the only order the venue will
    // ever see. `PRICE_TICK` is then a statement about a price that exists,
    // rather than about one this client invented.
    outcome: quoteOutcome,
    sizeShares,
    limitPriceCents: quoteLimitCents,
    coveredShares,
    quote,
    availableMicroUsdc,
    positionShares,
    symbol,
    openInterestShares: openInterestOf(ticker),
    openOrdersAccount: openOrders.account,
    openOrdersMarket: openOrders.market,
  }
  // What the order being composed needs, and which funding steps stand in the
  // way of it — `domain/trading-setup.ts`. Only once a key is armed: before
  // that, `ENABLE TRADING` is already the button and the modal finds the rest.
  const orderNeedMicroUsdc = requiredMicroUsdc(input)
  const setup =
    gate === 'READY' ?
      missingSetup({
        keyArmed: true,
        usdc: wallet.wrapper.backing,
        tradingBalance: wallet.wrapper.collateral,
        neededMicroUsdc: orderNeedMicroUsdc,
      })
    : []
  const decision = validateOrder({ ...input, setup })

  // One read when an armed ticket mounts, loading the wallet stack if it has
  // to: a user who has armed a key has used their wallet, and the setup button
  // must be there before they reach for the ticket, not after. Anonymous and
  // signed-out visitors never get here (invariant 13). Not a poll.
  const { refreshChainState } = wallet
  useEffect(() => {
    if (gate === 'READY') void refreshChainState(true)
  }, [gate, refreshChainState])

  /**
   * **What 100% on the slider is: the largest order that goes through.**
   *
   * For a market order that is the book walked with the user's money — every
   * share at the price it fills at, until the money or the book runs out,
   * whichever is first — plus, for a sell, the covered shares, which cost
   * nothing. It used to be `balance ÷ touch` for both kinds, which ignored the
   * price rising as an order walks the book and ignored the book running out:
   * against a 2,000-share ladder at 51–60¢ and a $1,934 balance, everything past
   * 53% of the slider was an order the ticket refused.
   *
   * For a limit order it is the balance at the limit, because a limit order
   * rests for the rest and locks at its own price. With no limit typed there is
   * nothing to size against and the slider is inert rather than guessing.
   */
  const sizingPriceCents = quoteLimitCents ?? quote.bestPriceCents
  const maxAffordable = useMemo(() => {
    if (orderType === 'MARKET') {
      return quoteOrder(book, {
        outcome: quoteOutcome,
        side: 'BUY',
        sizeShares: limits.maxOrderShares,
        limitPriceCents: null,
        takerFeeRate4dp: limits.takerFeeRate4dp,
        budgetMicroUsdc: availableMicroUsdc ?? 0n,
        freeShares: side === 'SELL' ? deliverableShares : 0n,
        sizeStep: limits.sizeStep,
      }).filledShares
    }
    const affordable = affordableShares(availableMicroUsdc, sizingPriceCents, limits)
    // **A sell is bounded by what can be sold, not only by what can be bought.**
    // The covered part costs nothing (§7.7), so the largest sell this account
    // can place is everything it holds *plus* whatever it could afford to mint.
    // Sizing a sell against the balance alone would offer a user a maximum
    // smaller than the position they are trying to dispose of.
    if (side !== 'SELL') return affordable
    const total = deliverableShares + affordable
    const capped = total > limits.maxOrderShares ? limits.maxOrderShares : total
    return floorToStep(capped, limits.sizeStep)
    // `bookVersion` for the reason `bookQuote` gives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, book, bookVersion, quoteOutcome, availableMicroUsdc, sizingPriceCents, limits, side, deliverableShares])
  const sliderPct = percentOf(sizeShares, maxAffordable)

  const setPercent = useCallback(
    (pct: number) => {
      if (maxAffordable <= 0n) return
      const target = floorToStep((maxAffordable * BigInt(Math.round(pct))) / 100n, limits.sizeStep)
      setSizeText(target <= 0n ? '' : trim(formatFixed(target, SHARES_DP)))
    },
    [maxAffordable, limits.sizeStep],
  )

  /**
   * The identity gates are actions, not dead ends: the button that says
   * `SIGN IN` signs in.
   */
  const remedy = useCallback(
    (code: GateCode, neededMicroUsdc: bigint) => {
      if (code === 'CONNECT') return void wallet.connect()
      // `SIGN IN AGAIN` — the wallet moved to an account the session is not for.
      // The header offers `wallet.signIn` for the same gate and this is the same
      // offer; `docs/07` §1 puts it above `ENABLE TRADING` in precedence, because
      // arming binds a key to the *session's* account.
      if (code === 'SIGN_IN' || code === 'WRONG_ACCOUNT') return void wallet.signIn()
      if (code === 'ENABLE_TRADING') return void wallet.promptEnableTrading(neededMicroUsdc)
      // `SWITCH NETWORK`: the click is the consent to switch, and the key's
      // signature follows once the switch lands, so it is one click.
      if (code === 'WRONG_CHAIN') return void wallet.switchAndEnableTrading(neededMicroUsdc)
    },
    [wallet],
  )

  const onPrimary = useCallback(async () => {
    if (isIdentityAction(decision.code)) return remedy(decision.code, orderNeedMicroUsdc)
    if (decision.blocked || !marketId) return
    if (inFlight.current) return

    // **A market order is signed as a marketable limit at its bound**, not at
    // the price this quote happened to reach — and since `docs/06` §F117
    // deleted the collar, the bound is the end of the price range, 99¢.
    //
    // The contract expresses price as `makerAmount / takerAmount`, so there is
    // no way to sign "at the market" (§F27d). The question is which number.
    // `worstPriceCents` — the deepest level *this* quote walked — signs a
    // tighter bound than `max_slippage_cents` in the same request body says the
    // venue may fill to, so the two halves of one order disagreed about the
    // user's own slippage tolerance, and the signature always won.
    //
    // Which mattered, because the signature is the bound the *contract*
    // enforces. The book moves between quote and match — a taker delay plus a
    // round trip — and a venue filling inside its own collar but above the
    // signed price produced a plausible off-chain fill whose on-chain match
    // reverted `NotCrossing`, leaving a fill with no transaction behind it
    // (§F28). Roughly half of them, since the drift is symmetric.
    //
    // `boundPriceCents` was `best + collar` until §F117; it is 99¢ now, so a
    // market order takes whatever the book has. Since §F94 no contract reads an
    // order, so the `NotCrossing` revert above can no longer happen; the
    // signature and the venue's walk still agree, because both stop at it.
    const signedPriceCents = orderType === 'LIMIT' ? limitPriceCents : quote.boundPriceCents
    if (signedPriceCents === null) return

    /**
     * What is actually placed: one leg for a buy, one or two for a sell.
     *
     * A buy's leg is the order itself. A sell's come from `splitSell`, which is
     * the whole of B7.5's rule and the only thing that decides whether a share
     * is transferred or minted.
     */
    const outbound: OrderLeg[] =
      side === 'SELL' ? legs : (
        [{ outcome, side: 'BUY', priceCents: signedPriceCents, sizeShares, rewritten: false }]
      )
    if (outbound.length === 0) return

    // Every leg needs its own position id, and there is no fallback: a market
    // whose token ids have not landed cannot be traded, because the signed
    // struct addresses the outcome by `tokenId` and nothing else.
    const tokenIds = outbound.map((leg) => (leg.outcome === 'UP' ? upTokenId : downTokenId))
    if (tokenIds.some((id) => id === null)) return
    // `validateOrder` already renders the ticket unavailable without it; this
    // is the guard a caller cannot reach around (docs/06 §F75).
    const expirationGraceMs = limits.orderExpirationGraceMs
    if (expirationGraceMs === null) return

    inFlight.current = true
    setSubmitting(true)
    try {
      for (const [i, leg] of outbound.entries()) {
        /**
         * **Two orders are two logical orders**, so each gets its own triple —
         * `client_order_id`, `Idempotency-Key`, `nonce_ms` — which is what
         * `OrderController.submit` mints per call (invariant 15). Sharing one
         * across the pair would make a retry of either a duplicate of the
         * other.
         *
         * Sequential, and it stops on a rejection or a self-match cancel (the
         * other leg is the same ask at the same price). The close is first
         * (`splitSell` puts it there) because it locks nothing and cannot be
         * refused for balance; if it is refused for anything else the minting
         * leg would be refused for the same reason, and placing it anyway
         * leaves the user holding half a disposal they did not ask for.
         */
        const result = await runtime.trading.submit({
          marketId,
          outcome: leg.outcome,
          side: leg.side,
          orderType,
          // A market order is `IOC` — it takes what it can and cancels the rest.
          // `MARKET` is `IOC`/`FOK` only (§7.1), and `FOK` on a 47-second book
          // turns a partial fill into no fill, which is not what a user pressing
          // a market button is asking for.
          timeInForce: orderType === 'MARKET' ? 'IOC' : 'GTC',
          priceCents: leg.priceCents,
          sizeShares: leg.sizeShares,
          postOnly: false,
          reduceOnly: false,
          // No slippage bound: there is no collar (`docs/06` §F117), and a
          // market order takes the book to the price it is signed at.
          maxSlippageCents: null,
          marketExpiryAtMs: expiryAtMs,
          expirationGraceMs,
          tokenId: BigInt(tokenIds[i] as string),
          feeRateBps: limits.feeRateBps,
        })
        if (result.status === 'REJECTED') break
        if (announceSelfMatch(result.order)) break
      }
    } finally {
      inFlight.current = false
      setSubmitting(false)
    }
  }, [
    decision,
    remedy,
    orderNeedMicroUsdc,
    marketId,
    runtime,
    outcome,
    side,
    legs,
    orderType,
    limitPriceCents,
    quote.boundPriceCents,
    sizeShares,
    limits.feeRateBps,
    limits.orderExpirationGraceMs,
    expiryAtMs,
    upTokenId,
    downTokenId,
  ])

  const primaryLabel = decision.label ?? `${side} ${outcome}`
  /**
   * The button's colour and glyph follow the **economic direction**, not the
   * outcome: selling UP is a bearish action and a green button would say
   * otherwise. `<Tape>` has rendered trades this way since Phase 6 for exactly
   * this reason — "a `SELL UP` at 63¢ and a `BUY DOWN` at 37¢ are both visible
   * as what the trader did".
   */
  const bullish = side === 'BUY' ? outcome === 'UP' : outcome === 'DOWN'
  const identityAction = isIdentityAction(decision.code)
  const buttonDisabled = decision.blocked && !identityAction
  const sellNote = side === 'SELL' ? describeSell(legs, sharesText) : null

  return (
    <div
      className={cx('flex flex-col gap-3', className)}
      onFocusCapture={onFocus}
      data-testid="order-ticket"
      data-gate={decision.code}
      data-side={side}
    >
      <SegmentedControl
        label="side"
        segments={SIDE_SEGMENTS}
        value={side}
        onChange={setSide}
      />

      <OutcomeToggle
        value={outcome}
        onChange={(next) => {
          if (next === outcome) return
          setOutcome(next)
          // 60¢ on UP is 40¢ on DOWN: the same order seen from the other side.
          setPriceText(priceTextForOtherOutcome)
        }}
        // A market order shows what it trades at — the touch it buys from or
        // sells into, which is what it and its one-tap presets pay. The
        // ticker's price is the last trade, and stays for a limit order, which
        // names its own price.
        upPrice={
          orderType === 'MARKET' ? centsOrNull(oneTapPrice(book, 'UP', side)) : centsText(ticker, 'up_price')
        }
        downPrice={
          orderType === 'MARKET' ? centsOrNull(oneTapPrice(book, 'DOWN', side)) : centsText(ticker, 'down_price')
        }
      />

      <SegmentedControl
        label="order type"
        segments={TYPE_SEGMENTS}
        value={orderType}
        onChange={(next) => {
          setOrderType(next)
          // Seed the limit from the touch, so the field is never empty at the
          // moment the user switches into it. It is the price they are looking
          // at, which is the only defensible default.
          if (next === 'LIMIT' && priceText === '' && quote.bestPriceCents !== null) {
            // In the user's own terms — the touch of the ladder they are
            // actually hitting, complemented back for a sell (§7.8).
            setPriceText(
              String(side === 'SELL' ? complementPriceCents(quote.bestPriceCents) : quote.bestPriceCents),
            )
          }
        }}
      />

      {orderType === 'LIMIT' ?
        <Input
          label="Price"
          value={priceText}
          onChange={(e) => setPriceText(e.target.value)}
          inputMode="numeric"
          unit="¢"
          numeric
        />
        // A market order has no price, so its slot holds the one-tap presets:
        // the same order placed by amount, in one tap (`docs/07` §1c).
      : <OneTapPanel
          marketId={marketId}
          side={side}
          outcome={outcome}
          book={book}
          bookVersion={bookVersion}
          limits={limits}
          deliverableShares={deliverableShares}
          gateInput={input}
          expiryAtMs={expiryAtMs}
          upTokenId={upTokenId}
          downTokenId={downTokenId}
        />
      }

      <Input
        label="Shares"
        value={sizeText}
        onChange={(e) => setSizeText(e.target.value)}
        inputMode="decimal"
        unit="shares"
        numeric
      />

      {linkBook ? <ClickToTradeToggle /> : null}

      <Slider
        label="size as a percentage of the largest order you can place"
        value={sliderPct}
        onChange={setPercent}
        tone={outcome === 'UP' ? 'up' : 'down'}
        disabled={maxAffordable <= 0n}
      />

      <PayoutPreview
        quote={quote}
        outcome={quoteOutcome}
        empty={sizeShares <= 0n}
        sell={
          side === 'SELL' ?
            {
              coveredShares,
              openingShares: sizeShares - coveredShares,
              proceedsMicroUsdc: saleProceedsMicroUsdc(coveredShares, quote.avgPriceCents4dp),
              sellPriceCents4dp: sellAvgCents4dp(quote.avgPriceCents4dp),
            }
          : null
        }
      />

      {sellNote ? (
        /**
         * The rewrite, said out loud before the click — `docs/07` §1.
         *
         * This is the entire cost of §F43's decision paid in one sentence. The
         * order row, the fill and the receipt will all say `BUY DOWN`, because
         * that is what the venue holds and what the user has now genuinely
         * placed; the sentence is what stops that being a surprise. It is
         * deliberately *not* a warning tone: nothing is wrong, and the payoff is
         * identical.
         */
        <p className="text-caption text-text-secondary" data-testid="sell-rewrite-note">
          {sellNote}
        </p>
      ) : null}

      {/*
        **The identity gates are pressable, and `blocked` is not what decides
        that.** `blocked` says no order may be placed, which is true of all four
        of them and is what guards `onPrimary`'s submit path above. Whether the
        *button* is dead is a different question: `CONNECT WALLET` connects,
        `SIGN IN` signs in, `ENABLE TRADING` opens the arming modal. Rendering
        them `disabled` made three handlers unreachable — and the arming one
        especially, because the automatic prompt fires once per ticket on first
        focus, so a user who dismissed it had no second way in.
      */}
      {/*
        **The primary button never scrolls out of reach** (2026-10-13). On a
        short window the ticket scrolls inside its column, and the column's room
        no longer depends on anything live, so this is what keeps the action on
        screen at every height: the button and the line under it stick to the
        foot of the scroll area, over the fields above. At rest they sit where
        they always did — the negative margins hand back the padding that keeps
        what scrolls under them off their edges.
      */}
      <div
        data-testid="ticket-action"
        className="sticky bottom-0 z-10 -my-2 flex flex-col gap-3 bg-bg-surface py-2"
      >
        <Button
          variant={identityAction ? 'primary' : bullish ? 'up' : 'down'}
          size="lg"
          block
          loading={submitting}
          disabled={buttonDisabled}
          {...(decision.helper && buttonDisabled ? { disabledReason: decision.helper } : {})}
          onClick={() => void onPrimary()}
        >
          {decision.label ? primaryLabel : (
            <>
              <Glyph name={bullish ? 'up' : 'down'} label={null} /> {primaryLabel}
            </>
          )}
        </Button>

        <HelperLine decision={decision} onUseMax={(shares) => setSizeText(trim(formatFixed(shares, SHARES_DP)))} />
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------

/**
 * The "Click to trade" box, under Shares — the switch for the orderbook beside
 * the ticket.
 *
 * Under Shares because Shares is what a click places. Smaller than the rest of
 * the ticket on purpose (`text-micro`): it is a setting about the panel next
 * door, not a field of this order. A native checkbox — a `role` is a promise
 * about the keyboard (invariant 20), and this one keeps it for free: Space
 * toggles it and its state is announced.
 */
function ClickToTradeToggle() {
  const on = useStoreField(uiStore, (s) => s.clickToTrade)
  return (
    <label
      className="-mt-1_5 flex w-fit cursor-pointer items-center gap-1_5 text-micro text-text-secondary"
      title="Click a price in the orderbook to trade it, using the shares above"
    >
      <input
        type="checkbox"
        checked={on}
        onChange={(event) => setClickToTrade(event.target.checked)}
        className="size-3 cursor-pointer accent-accent-solid focus-visible:shadow-focus focus-visible:outline-none"
      />
      Click to trade
    </label>
  )
}

/**
 * The tightest order allowance the venue has reported, or `null`.
 *
 * `null` when no policy has ever been seen, which is the state a tab is in until
 * it has placed its first order — and is why this predicts nothing rather than
 * blocking anything. `docs/03` §9.2.10 is precise about the direction: the point
 * is to avoid firing into a 429, never to invent one.
 */
function orderAllowance(
  runtime: Runtime,
  policies: readonly string[],
): { remaining: number; resetAtMs: number | null } | null {
  let tightest: { remaining: number; resetAtMs: number | null } | null = null
  for (const policy of policies) {
    const allowance = runtime.http.allowance(policy)
    if (!allowance) continue
    if (!tightest || allowance.remaining < tightest.remaining) {
      tightest = { remaining: allowance.remaining, resetAtMs: allowance.resetAtMs }
    }
  }
  return tightest
}

/**
 * The helper line, and the one affordance `docs/07` §1 attaches to it.
 *
 * `[Use max]` is offered on the position-cap row and nowhere else, because it is
 * the only row where the remedy is a number the client already knows.
 */
function HelperLine({
  decision,
  onUseMax,
}: {
  decision: OrderGate
  onUseMax: (shares: bigint) => void
}) {
  if (!decision.helper) return null
  const action = decision.action
  return (
    <p className="text-caption text-text-secondary" data-testid="ticket-helper">
      {decision.helper}{' '}
      {action?.kind === 'USE_MAX' && action.sizeShares > 0n ? (
        <button
          type="button"
          onClick={() => onUseMax(action.sizeShares)}
          className="font-semibold text-accent-text underline-offset-2 hover:underline focus-visible:shadow-focus focus-visible:outline-none"
        >
          Use max
        </button>
      ) : null}
      {action?.kind === 'SUPPORT' ? (
        <a
          href="mailto:support@updown.xyz"
          className="font-semibold text-accent-text underline-offset-2 hover:underline"
        >
          Contact support
        </a>
      ) : null}
    </p>
  )
}


function centsOf(text: string): number | null {
  const trimmed = text.trim()
  if (!/^\d{1,3}$/.test(trimmed)) return null
  return Number(trimmed)
}

/** The largest legal size the available balance covers at `priceCents`. */

/** Where the slider's thumb sits, given a size that was typed rather than dragged. */
function percentOf(sizeShares: bigint, maxShares: bigint): number {
  if (maxShares <= 0n || sizeShares <= 0n) return 0
  const pct = Number((sizeShares * 100n) / maxShares)
  return Math.max(0, Math.min(100, pct))
}

function trim(value: string): string {
  return value.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')
}

function sharesText(micro: bigint): string {
  return formatShares(formatFixed(micro, SHARES_DP))
}

/** The complement's average, back in the user's own terms. `100.0000 − avg`. */
function sellAvgCents4dp(avgCents4dp: bigint | null): bigint | null {
  return avgCents4dp === null ? null : 1_000_000n - avgCents4dp
}


/**
 * A ticker price for one half of `<OutcomeToggle>`, or `null`.
 *
 * `null` rather than `'—'`: the toggle renders no price line at all when there
 * is none, because a placeholder dash under the button the user is about to
 * press is a mark with no meaning. An empty book and a market that has not
 * traded are both this case, and on a quiet venue they are most of the minute.
 */
function centsText(ticker: unknown, field: 'up_price' | 'down_price'): string | null {
  const value = (ticker as Record<string, unknown> | null)?.[field]
  return typeof value === 'number' ? formatCents(value) : null
}

/** A price for `<OutcomeToggle>`, or `null` for no price line — `centsText`'s rule. */
function centsOrNull(priceCents: number | null): string | null {
  return priceCents === null ? null : formatCents(priceCents)
}



